import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Settings from '../Settings';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import Property from '@afosto/app-components-react/Property';
import SchemaForm from '@afosto/app-components-react/SchemaForm';
import currentUnixTimestamp from '@afosto/utils/date/currentUnixTimestamp';
import { locations as locationsApi } from '@afosto/wms/api/Locations';
import { methods as methodsApi } from '@afosto/fulfilment/api/Methods';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import translations from './translations';

const TRANSFER_PATH = 'afosto.client.wms.claim.transfer';

const getResolvedSettings = async data => {
  try {
    const resolvedSettings = {};
    if (data?.inbound_location_id) {
      const { data: locationData = {} } = await locationsApi
        .getLocation(data.inbound_location_id)
        .catch(() => {});
      resolvedSettings.inboundLocation = {
        label: locationData.name,
        value: locationData.id,
        ...locationData,
      };
    }

    if (data?.collection_location_id) {
      const { data: locationData = {} } = await locationsApi
        .getLocation(data.collection_location_id)
        .catch(() => {});
      resolvedSettings.collectionLocation = {
        label: locationData.name,
        value: locationData.id,
        ...locationData,
      };
    }

    if (data?.internal_shipment_method_id) {
      const { data: shippingMethodData = {} } = await methodsApi
        .getShippingMethod(data.internal_shipment_method_id)
        .catch(() => {});
      resolvedSettings.internalShippingMethod = {
        label: shippingMethodData.name,
        value: shippingMethodData.id,
        ...shippingMethodData,
      };
    }

    if (data?.backorder_inbound_location_id) {
      const { data: locationData = {} } = await locationsApi
        .getLocation(data.backorder_inbound_location_id)
        .catch(() => {});
      resolvedSettings.backOrderInboundLocation = {
        label: locationData.name,
        value: locationData.id,
        ...locationData,
      };
    }
    return resolvedSettings;
  } catch (error) {
    return Promise.reject(error);
  }
};

const TransferSettings = ({ pathID }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['wms:transfer:read']);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [transferSettings, setTransferSettings] = useState(null);
  const [formSchema, setFormSchema] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const hasReadPermissions = hasPermissions();

  const uiSchema = {
    inbound_location_id: {
      component: 'AsyncAutocomplete',
      props: {
        label: intl.formatMessage(translations.editDrawer.inboundField.label),
        url: '/wms/locations',
        valueOnly: true,
        formatOptions: options =>
          options.map(option => ({ ...option, label: option.name, value: option.id })),
        requestOptions: {
          params: {
            deleted_at: currentUnixTimestamp(),
          },
          headers: {
            'x-page-size': 250,
          },
        },
      },
    },
    collection_location_id: {
      component: 'AsyncAutocomplete',
      props: {
        label: intl.formatMessage(translations.editDrawer.collectionField.label),
        url: '/wms/locations',
        valueOnly: true,
        formatOptions: options =>
          options.map(option => ({ ...option, label: option.name, value: option.id })),
        requestOptions: {
          params: {
            deleted_at: currentUnixTimestamp(),
          },
          headers: {
            'x-page-size': 250,
          },
        },
      },
    },
    internal_shipment_method_id: {
      component: 'AsyncAutocomplete',
      props: {
        label: intl.formatMessage(translations.editDrawer.internalShippingMethodField.label),
        url: '/lcs/methods',
        valueOnly: true,
        formatOptions: options =>
          options.map(option => ({ ...option, label: option.name, value: option.id })),
        requestOptions: {
          headers: {
            'x-page-size': 250,
          },
        },
      },
    },
    backorder_inbound_location_id: {
      component: 'AsyncAutocomplete',
      props: {
        label: intl.formatMessage(translations.editDrawer.backOrderInboundLocationField.label),
        url: '/wms/locations',
        valueOnly: true,
        formatOptions: options =>
          options.map(option => ({ ...option, label: option.name, value: option.id })),
        requestOptions: {
          params: {
            deleted_at: currentUnixTimestamp(),
          },
          headers: {
            'x-page-size': 250,
          },
        },
      },
    },
  };

  const handleUpdateProfile = async fields => {
    try {
      const payload = Object.keys(fields).reduce(
        (acc, field) => ({
          ...acc,
          [field]: fields[field],
        }),
        {},
      );
      const { data: profileData = {} } = await profileApi.updateProfile(
        TRANSFER_PATH.replace('client', pathID),
        payload,
      );

      const resolvedSettings = await getResolvedSettings(profileData);

      setTransferSettings(resolvedSettings);
      enqueueSnackbar(intl.formatMessage(translations.feedback.transferSettingsUpdated), {
        variant: 'success',
      });
      setIsEditing(false);
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.transferSettingsNotUpdated), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const handleFetchProfile = async () => {
      try {
        const { data: profileData = {} } = await profileApi.getProfile(
          TRANSFER_PATH.replace('client', pathID),
        );

        const resolvedSettings = await getResolvedSettings(profileData);

        if (isMounted) {
          setTransferSettings(resolvedSettings);
        }
      } catch (error) {
        if (isMounted) {
          setTransferSettings(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const fetchProfileFormSchema = async () => {
      try {
        const { data } = await profileApi.getProfileDefinitions(
          TRANSFER_PATH.replace('client', pathID),
        );
        delete data.properties.version;

        if (isMounted) {
          setFormSchema(data);
        }
      } catch (error) {
        if (isMounted) {
          setFormSchema({});
        }
      }
    };

    if (hasReadPermissions) {
      handleFetchProfile();
      fetchProfileFormSchema();
    }

    return () => {
      isMounted = false;
    };
  }, [pathID, hasReadPermissions]);

  if (!hasReadPermissions) {
    return (
      <Settings title={intl.formatMessage(translations.title)} isLoading={false}>
        <UnauthorizedComponent />
      </Settings>
    );
  }

  return (
    <>
      <Settings title={intl.formatMessage(translations.title)} isLoading={isLoading}>
        <List dense disablePadding>
          <ListItem>
            <ListItemText
              primary={
                <>
                  <Property
                    label={intl.formatMessage(translations.editDrawer.inboundField.label)}
                    value={transferSettings?.inboundLocation?.name}
                  />
                  <Property
                    label={intl.formatMessage(translations.editDrawer.collectionField.label)}
                    value={transferSettings?.collectionLocation?.name}
                  />
                  <Property
                    label={intl.formatMessage(
                      translations.editDrawer.internalShippingMethodField.label,
                    )}
                    value={transferSettings?.internalShippingMethod?.name}
                  />
                  <Property
                    label={intl.formatMessage(
                      translations.editDrawer.backOrderInboundLocationField.label,
                    )}
                    value={transferSettings?.backOrderInboundLocation?.name}
                  />
                </>
              }
            />
            {hasPermissions(['wms:transfer:write']) && (
              <Button onClick={() => setIsEditing(true)} variant="outlined" size="small">
                <FormattedMessage {...translations.actions.edit} />
              </Button>
            )}
          </ListItem>
        </List>
      </Settings>
      <FormDrawer
        isOpen={isEditing}
        title={intl.formatMessage(translations.editDrawer.title)}
        drawerWidth="420px"
        onClose={() => setIsEditing(false)}
      >
        {formSchema && (
          <SchemaForm
            schema={formSchema}
            uiSchema={uiSchema}
            values={{
              inbound_location_id: transferSettings?.inboundLocation?.id,
              collection_location_id: transferSettings?.collectionLocation?.id,
              internal_shipment_method_id: transferSettings?.internalShippingMethod?.id,
              backorder_inbound_location_id: transferSettings?.backOrderInboundLocation?.id,
            }}
            onSubmit={handleUpdateProfile}
            onCancel={() => setIsEditing(false)}
          />
        )}
      </FormDrawer>
    </>
  );
};

TransferSettings.propTypes = {
  pathID: PropTypes.string,
};

TransferSettings.defaultProps = {
  pathID: 'client',
};

export default TransferSettings;
